import React, { useMemo, useState } from 'react';
import { buildClientDisplayName } from '../serviceOptions';
import { resolveWeekdayLabel } from '../serviceBilling';

const parseTimeToMinutes = (value = '') => {
  const [hours, minutes] = String(value || '').split(':').map((item) => Number(item || 0));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

export default function ServiceCalendarView({
  clients = [],
  contracts = [],
  templates = [],
  transports = [],
  drivers = [],
  activatingTemplateId = '',
  onActivateTemplate,
}) {
  const [viewMode, setViewMode] = useState('global');
  const [clientFilter, setClientFilter] = useState('all');

  const transportNameById = useMemo(() => {
    const map = new Map();
    transports.forEach((transport) => {
      map.set(String(transport?.id || '').trim(), transport?.name || transport?.displayName || transport?.plate || transport?.id || 'Unidad');
    });
    return map;
  }, [transports]);

  const driverNameById = useMemo(() => {
    const map = new Map();
    drivers.forEach((driver) => {
      map.set(
        String(driver?.id || '').trim(),
        [driver?.firstName || driver?.name || '', driver?.lastName || ''].filter(Boolean).join(' ').trim() || driver?.id || 'Chofer'
      );
    });
    return map;
  }, [drivers]);

  const items = useMemo(() => {
    const contractItems = contracts
      .filter((contract) => String(contract?.status || '').trim().toLowerCase() === 'active')
      .map((contract) => ({
        id: `contract-${contract.id}`,
        source: 'contract',
        clientId: String(contract?.clientId || '').trim(),
        clientName: contract?.clientNameSnapshot || clients.find((client) => client.id === contract?.clientId)?.name || 'Cliente',
        title: contract?.name || 'Contrato',
        cadence: contract?.billingCadence || 'per_trip',
        days: Array.isArray(contract?.serviceDaysOfWeek) ? contract.serviceDaysOfWeek : [],
        start: contract?.serviceTimeWindowStart || '',
        end: contract?.serviceTimeWindowEnd || '',
        transportAssignmentMode: contract?.transportAssignmentMode || 'free',
        fixedTransportId: contract?.fixedTransportId || '',
        fixedTransportName: contract?.fixedTransportNameSnapshot || transportNameById.get(String(contract?.fixedTransportId || '').trim()) || '',
        suggestedTransportId: contract?.suggestedTransportId || '',
        suggestedTransportName: contract?.suggestedTransportNameSnapshot || transportNameById.get(String(contract?.suggestedTransportId || '').trim()) || '',
        driverAssignmentMode: contract?.driverAssignmentMode || 'free',
        fixedDriverId: contract?.fixedDriverId || '',
        fixedDriverName: contract?.fixedDriverNameSnapshot || driverNameById.get(String(contract?.fixedDriverId || '').trim()) || '',
        suggestedDriverId: contract?.suggestedDriverId || '',
        suggestedDriverName: contract?.suggestedDriverNameSnapshot || driverNameById.get(String(contract?.suggestedDriverId || '').trim()) || '',
        billingDayOfWeek: contract?.billingDayOfWeek || '',
        billingDayOfMonth: contract?.billingDayOfMonth || '',
      }));

    const templateItems = templates.map((template) => ({
      id: `template-${template.id}`,
      source: 'template',
      templateSource: template,
      clientId: String(template?.clientId || '').trim(),
      clientName: template?.clientNameSnapshot || 'Cliente',
      title: template?.serviceTaskLabel || template?.serviceModeLabel || 'Servicio programado',
      cadence: template?.billingCadenceSnapshot || 'per_trip',
      days: Array.isArray(template?.serviceDaysOfWeek) ? template.serviceDaysOfWeek : [],
      start: template?.serviceTimeWindowStart || '',
      end: template?.serviceTimeWindowEnd || '',
      transportAssignmentMode: template?.transportAssignmentModeSnapshot || 'free',
      fixedTransportId: template?.fixedTransportIdSnapshot || '',
      fixedTransportName: template?.fixedTransportNameSnapshot || transportNameById.get(String(template?.fixedTransportIdSnapshot || '').trim()) || '',
      suggestedTransportId: template?.suggestedTransportIdSnapshot || '',
      suggestedTransportName: template?.suggestedTransportNameSnapshot || transportNameById.get(String(template?.suggestedTransportIdSnapshot || '').trim()) || '',
      driverAssignmentMode: template?.driverAssignmentModeSnapshot || 'free',
      fixedDriverId: template?.fixedDriverIdSnapshot || '',
      fixedDriverName: template?.fixedDriverNameSnapshot || driverNameById.get(String(template?.fixedDriverIdSnapshot || '').trim()) || '',
      suggestedDriverId: template?.suggestedDriverIdSnapshot || '',
      suggestedDriverName: template?.suggestedDriverNameSnapshot || driverNameById.get(String(template?.suggestedDriverIdSnapshot || '').trim()) || '',
      billingDayOfWeek: template?.billingDayOfWeek || '',
      billingDayOfMonth: template?.billingDayOfMonth || '',
    }));

    return [...contractItems, ...templateItems]
      .filter((item) => (clientFilter === 'all' ? true : item.clientId === clientFilter))
      .sort((a, b) => a.clientName.localeCompare(b.clientName, 'es'));
  }, [clientFilter, clients, contracts, templates, transportNameById, driverNameById]);

  const conflicts = useMemo(() => {
    const flagged = new Set();

    items.forEach((current, index) => {
      if (current.transportAssignmentMode !== 'fixed' || !current.fixedTransportId) return;
      const currentDays = Array.isArray(current.days) ? current.days : [];
      const currentStart = parseTimeToMinutes(current.start);
      const currentEnd = parseTimeToMinutes(current.end);

      items.slice(index + 1).forEach((other) => {
        if (other.transportAssignmentMode !== 'fixed' || current.fixedTransportId !== other.fixedTransportId) return;
        const otherDays = Array.isArray(other.days) ? other.days : [];
        const sharedDay = currentDays.some((day) => otherDays.includes(day));
        if (!sharedDay) return;
        const otherStart = parseTimeToMinutes(other.start);
        const otherEnd = parseTimeToMinutes(other.end);
        if (currentStart === null || currentEnd === null || otherStart === null || otherEnd === null) {
          flagged.add(current.id);
          flagged.add(other.id);
          return;
        }
        const overlaps = currentStart < otherEnd && otherStart < currentEnd;
        if (overlaps) {
          flagged.add(current.id);
          flagged.add(other.id);
        }
      });
    });

    return flagged;
  }, [items]);

  const groupedByDay = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const days = item.days.length > 0 ? item.days : ['on_demand'];
      days.forEach((day) => {
        if (!map.has(day)) {
          map.set(day, []);
        }
        map.get(day).push(item);
      });
    });
    return Array.from(map.entries()).sort(([a], [b]) => {
      const order = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'on_demand'];
      return order.indexOf(a) - order.indexOf(b);
    });
  }, [items]);

  return (
    <div className="space-y-4">
      <div className="rounded-[30px] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Calendario operativo</p>
            <h3 className="mt-2 text-xl font-black tracking-tight text-slate-900">Agenda global y por cliente</h3>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Leemos días pactados, franjas horarias, transporte fijo o sugerido y avisamos si una unidad ya está ocupada en ese tramo.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setViewMode('global')} className={`rounded-full border px-4 py-2 text-sm font-semibold ${viewMode === 'global' ? 'border-cyan-300 bg-cyan-600 text-white' : 'border-slate-200 bg-white text-slate-600'}`}>
              Global
            </button>
            <button type="button" onClick={() => setViewMode('client')} className={`rounded-full border px-4 py-2 text-sm font-semibold ${viewMode === 'client' ? 'border-cyan-300 bg-cyan-600 text-white' : 'border-slate-200 bg-white text-slate-600'}`}>
              Por cliente
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_240px]">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 shadow-sm">
            {conflicts.size > 0
              ? `Detectamos ${conflicts.size} servicio${conflicts.size === 1 ? '' : 's'} con posible superposición de transporte fijo.`
              : 'No vemos conflictos de transporte fijo para lo ya programado.'}
          </div>
          <label className="block space-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cliente</span>
            <select value={clientFilter} onChange={(event) => setClientFilter(event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100">
              <option value="all">Todos los clientes</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {buildClientDisplayName(client)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {viewMode === 'global' ? (
        <div className="space-y-4">
          {groupedByDay.map(([day, dayItems]) => (
            <div key={day} className="rounded-[28px] border border-slate-200 bg-white px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Día</p>
                  <p className="mt-1 text-lg font-black text-slate-900">{day === 'on_demand' ? 'A pedido / libre' : resolveWeekdayLabel(day)}</p>
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                  {dayItems.length} servicio{dayItems.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="mt-4 grid gap-3 xl:grid-cols-2">
                {dayItems.map((item) => (
                  <CalendarCard
                    key={item.id}
                    item={item}
                    hasConflict={conflicts.has(item.id)}
                    activatingTemplateId={activatingTemplateId}
                    onActivateTemplate={onActivateTemplate}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <CalendarCard
              key={item.id}
              item={item}
              hasConflict={conflicts.has(item.id)}
              detailed
              activatingTemplateId={activatingTemplateId}
              onActivateTemplate={onActivateTemplate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CalendarCard({ item, hasConflict = false, detailed = false, activatingTemplateId = '', onActivateTemplate }) {
  return (
    <div className={`rounded-[24px] border px-4 py-4 shadow-sm ${hasConflict ? 'border-amber-300 bg-amber-50/70' : 'border-slate-200 bg-slate-50/70'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">
              {item.source === 'contract' ? 'Contrato' : 'Plantilla'}
            </span>
            <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${item.cadence === 'per_trip' ? 'border-cyan-200 bg-cyan-50 text-cyan-700' : 'border-violet-200 bg-violet-50 text-violet-700'}`}>
              {item.cadence === 'weekly' ? 'Semanal' : item.cadence === 'monthly' ? 'Mensual' : 'Por viaje'}
            </span>
            {hasConflict ? (
              <span className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                Conflicto
              </span>
            ) : null}
          </div>
          <p className="mt-3 text-lg font-black tracking-tight text-slate-950">{item.clientName}</p>
          <p className="mt-1 text-sm text-slate-500">{item.title}</p>
        </div>
        <div className="rounded-[18px] border border-white bg-white px-3 py-2 text-right shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Horario</p>
          <p className="mt-1 text-sm font-black text-slate-900">
            {item.start && item.end ? `${item.start} a ${item.end}` : item.start ? `Desde ${item.start}` : 'Sin franja'}
          </p>
        </div>
      </div>
      <div className={`mt-4 grid gap-3 ${detailed ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        <InfoCard label="Asignación" value={item.transportAssignmentMode === 'fixed' ? 'Transporte fijo' : item.transportAssignmentMode === 'suggested' ? 'Sugerida' : 'Libre'} />
        <InfoCard
          label="Unidad"
          value={
            item.transportAssignmentMode === 'fixed'
              ? item.fixedTransportName || 'Sin unidad'
              : item.transportAssignmentMode === 'suggested'
              ? item.suggestedTransportName || 'Sin sugerencia'
              : 'Se define al activar'
          }
        />
        <InfoCard
          label="Chofer"
          value={
            item.driverAssignmentMode === 'fixed'
              ? item.fixedDriverName || 'Sin chofer'
              : item.driverAssignmentMode === 'suggested'
              ? item.suggestedDriverName || 'Sin sugerencia'
              : 'Se define al activar'
          }
        />
        {detailed ? (
          <InfoCard
            label="Cobro"
            value={
              item.cadence === 'weekly'
                ? `Cada ${resolveWeekdayLabel(item.billingDayOfWeek || 'friday')}`
                : item.cadence === 'monthly'
                ? `Día ${item.billingDayOfMonth || '-'}`
                : 'Por viaje'
            }
          />
        ) : null}
      </div>
      {item.source === 'template' && typeof onActivateTemplate === 'function' ? (
        <div className="mt-4 flex justify-end border-t border-white/80 pt-4">
          <button
            type="button"
            onClick={() => onActivateTemplate(item.templateSource)}
            disabled={activatingTemplateId === item.templateSource?.id}
            className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {activatingTemplateId === item.templateSource?.id ? 'Activando...' : 'Activar viaje'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-[18px] border border-white bg-white px-3 py-2 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-bold text-slate-900">{value}</p>
    </div>
  );
}
